// Preview as client (BA P1.6a, owner AC3): fetches the CLIENT VIEW from the API
// (P1.8 preview) and renders exactly that — no local rendering of draft or
// revision data. Explicit loading / nothing-published / not-available / error.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataState } from '@/features/shared/components/ui/DataState';
import { getApiErrorMessage } from '@/lib/apiError';
import type { BaPreview } from '@/services/baProfileService';
import { ClientProfileView } from './ClientProfileView';

type State = { s: 'loading' } | { s: 'ok'; p: BaPreview } | { s: 'none' } | { s: 'unavailable' } | { s: 'error'; msg: string };

export const ClientPreview: React.FC<{
  load: () => Promise<BaPreview>;
  /** Changes whenever the previewed thing may have changed (save, submit, …). */
  reloadKey: unknown;
  company: string;
}> = ({ load, reloadKey, company }) => {
  const { t } = useTranslation('brandAmbassadors');
  const [st, setSt] = useState<State>({ s: 'loading' });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let live = true;
    setSt({ s: 'loading' });
    load()
      .then((p) => live && setSt({ s: 'ok', p }))
      .catch((e) => {
        if (!live) return;
        const r = e?.response;
        if (r?.status === 404) {
          // nothing_published: nothing live to show; any other 404: the preview isn't available here.
          setSt(r?.data?.detail?.code === 'nothing_published' ? { s: 'none' } : { s: 'unavailable' });
          return;
        }
        setSt({ s: 'error', msg: getApiErrorMessage(e, t('errors.preview', { defaultValue: 'Could not load the preview.' }) as string) });
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, retry]);

  if (st.s === 'loading') return <DataState variant="loading" compact />;
  if (st.s === 'none') return <DataState variant="empty" title={t('preview.nothing_published', { defaultValue: 'Nothing is published yet.' }) as string} />;
  if (st.s === 'unavailable') {
    return <DataState variant="unavailable" message={t('preview.unavailable', { defaultValue: 'The client preview isn’t available yet.' }) as string} />;
  }
  if (st.s === 'error') return <DataState variant="error" message={st.msg} onRetry={() => setRetry((n) => n + 1)} />;
  return <ClientProfileView view={st.p.view} source={st.p.source} company={company} />;
};
